const supportedTypes = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const convertedFiles = new WeakMap();
const conversionError = "This photo could not be converted. Choose a JPEG, PNG or WebP photo and try again.";

export const needsGuidePhotoConversion = (file) => file.type
  ? !supportedTypes.has(file.type.toLowerCase())
  : !/\.(jpe?g|png|webp|gif)$/i.test(file.name || "");

// AVIF and other browser-readable formats must be encoded as JPEG before
// upload: the AI service cannot analyze the original bytes. Keep supported
// originals unchanged and reuse conversions between analysis and saving.
export const prepareGuidePhotoFile = (file) => {
  if (!needsGuidePhotoConversion(file)) return Promise.resolve(file);
  if (convertedFiles.has(file)) return convertedFiles.get(file);
  const conversion = new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    const fail = () => {
      URL.revokeObjectURL(url);
      reject(new Error(conversionError));
    };
    image.onerror = fail;
    image.onload = () => {
      try {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) throw new Error(conversionError);
        const scale = Math.min(1, 4096 / Math.max(width, height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const context = canvas.getContext("2d");
        if (!context) throw new Error(conversionError);
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (!blob || blob.type !== "image/jpeg") return fail();
          URL.revokeObjectURL(url);
          const name = `${(file.name || "photo").replace(/\.[^.]+$/, "")}.jpg`;
          resolve(new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified }));
        }, "image/jpeg", 0.92);
      } catch (_) {
        fail();
      }
    };
    image.src = url;
  });
  convertedFiles.set(file, conversion);
  conversion.catch(() => convertedFiles.delete(file));
  return conversion;
};
